<template>
    <section
        id="showroom"
        class="showroom"
    >

        <div class="container">

            <div class="showroom__header">

                <span class="showroom__eyebrow">
                    {{ 'home_showroom' | staticContentAlt }}
                </span>

                <h2>
                    {{ 'home_showroom' | staticTitle }}
                </h2>

                <p>
                    {{ 'home_showroom' | staticContent }}
                </p>

            </div>

            <div class="showroom__grid">

                <article
                    v-for="(item, index) in showroomItems"
                    :key="item.hash"
                    class="showroom-card"
                >

                    <div class="showroom-card__media">

                        <img
                            v-if="item.image"
                            :src="item.image"
                            :alt="item.title"
                        >

                        <div class="showroom-card__overlay"></div>

                        <span class="showroom-card__index">
                            0{{ index + 1 }}
                        </span>

                    </div>

                    <div class="showroom-card__content">

                        <span
                            v-if="item.contentAlt"
                            class="showroom-card__category"
                        >
                            {{ item.contentAlt }}
                        </span>

                        <h3>
                            {{ item.title }}
                        </h3>

                        <p>
                            {{ item.content }}
                        </p>

                        <router-link
                            :to="item.route"
                            class="showroom-card__link"
                        >
                            {{ 'home_showroom_actions' | staticTitle }}

                            <span>
                                ↗
                            </span>
                        </router-link>

                    </div>

                </article>

            </div>

        </div>

    </section>
</template>

<script>
import { imageIdToURL } from '@/utils';

export default {
    name: 'HomeShowroom',

    computed: {
        showroomItems() {
            const categories = [
                {
                    hash: 'showroom-web',
                    route: '/portfolio/web'
                },
                {
                    hash: 'showroom-apps',
                    route: '/portfolio/apps'
                },
                {
                    hash: 'showroom-business',
                    route: '/portfolio/business'
                }
            ];

            return categories
                .map(category => {

                    const items =
                        this.$options.filters.portfolioFetchBy(
                            category.hash
                        );

                    if (
                        !Array.isArray(items) ||
                        !items.length
                    ) {
                        return null;
                    }

                    const item = items[0];

                    return {
                        hash: category.hash,

                        route:
                            category.route,

                        title:
                            item.title || '',

                        content:
                            item.content || '',

                        contentAlt:
                            item.content_alt || '',

                        image:
                            item.imagenes_id
                                ? imageIdToURL(
                                    item.imagenes_id
                                )
                                : ''
                    };
                })
                .filter(Boolean);
        }
    }
};
</script>

<style lang="scss" scoped>
.showroom {
    position: relative;

    padding:
        5.625em 1.25em
        6.25em;

    background:
        radial-gradient(
            circle at 15% 15%,
            rgba(233, 81, 29, .05),
            transparent 28%
        ),
        var(--color-2);

    border-top:
        0.0625em solid
        var(--border-1);

    overflow: hidden;
}

.showroom::before {
    content: "";

    position: absolute;

    top: -15.625em;
    right: -13.75em;

    width: 32.5em;
    height: 32.5em;

    border:
        0.0625em solid
        rgba(255, 255, 255, .035);

    border-radius: 50%;

    pointer-events: none;
}

.container {
    position: relative;

    width: 100%;
    max-width: var(--container);

    margin: 0 auto;

    z-index: 2;
}

.showroom__header {
    max-width: 47.5em;

    margin:
        0 auto
        3.75em;

    text-align: center;

    h2 {
        margin:
            0 0
            1.125em;

        color: var(--text-1);

        font-size:
            clamp(
                2.375em,
                7vw,
                4em
            );

        font-weight: 700;
        line-height: .95;
        -letter-spacing: -0.125em;

        text-transform: uppercase;
    }

    p {
        max-width: 38.75em;

        margin: 0 auto;

        color: var(--text-2);

        font-size: 0.875em;
        line-height: 1.8;
    }
}

.showroom__eyebrow {
    display: inline-flex;
    align-items: center;

    gap: 0.75em;

    margin-bottom: 1em;

    color: var(--text-3);

    font-family: var(--font-tech);
    font-size: 0.625em;
    font-weight: 600;
    -letter-spacing: 0.15625em;

    text-transform: uppercase;

    &::before,
    &::after {
        content: "";

        width: 1.5em;
        height: 0.0625em;

        background: var(--color-3);
    }
}

.showroom__grid {
    display: grid;
    grid-template-columns: 1fr;

    gap: 1.375em;
}

.showroom-card {
    position: relative;

    display: flex;
    flex-direction: column;

    min-width: 0;

    border:
        0.0625em solid
        var(--border-1);

    background:
        linear-gradient(
            145deg,
            rgba(255, 255, 255, .018),
            rgba(255, 255, 255, .006)
        );

    overflow: hidden;

    transition:
        transform .35s ease,
        border-color .35s ease,
        background .35s ease;

    &:hover {
        transform:
            translateY(-0.3125em);

        border-color:
            rgba(233, 81, 29, .34);

        background:
            rgba(255, 255, 255, .025);
    }
}

.showroom-card__media {
    position: relative;

    aspect-ratio: 16 / 10;

    background: var(--color-1);

    overflow: hidden;

    img {
        display: block;

        width: 100%;
        height: 100%;

        object-fit: cover;

        transition:
            transform .5s ease,
            filter .5s ease;
    }
}

.showroom-card:hover
.showroom-card__media img {
    transform:
        scale(1.045);

    filter:
        brightness(.88);
}

.showroom-card__overlay {
    position: absolute;

    inset: 0;

    background:
        linear-gradient(
            180deg,
            rgba(1, 16, 36, .02) 0%,
            rgba(1, 16, 36, .14) 55%,
            rgba(1, 16, 36, .52) 100%
        );

    pointer-events: none;
}

.showroom-card__index {
    position: absolute;

    top: 1.125em;
    left: 1.125em;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 2.125em;
    height: 2.125em;

    border:
        0.0625em solid
        rgba(255, 255, 255, .15);

    background:
        rgba(1, 16, 36, .55);

    color: var(--text-3);

    font-family: var(--font-tech);
    font-size: 0.5625em;
    -letter-spacing: 0.0625em;

    backdrop-filter: blur(0.5em);
    -webkit-backdrop-filter: blur(0.5em);
}

.showroom-card__content {
    display: flex;
    flex-direction: column;

    flex: 1;

    padding:
        1.625em 1.5em
        1.5em;

    h3 {
        margin:
            0 0
            0.75em;

        color: var(--text-1);

        font-size: 1.375em;
        font-weight: 600;
        line-height: 1.2;

        text-transform: uppercase;
    }

    p {
        margin:
            0 0
            1.5em;

        color: var(--text-2);

        font-size: 0.8125em;
        line-height: 1.7;
    }
}

.showroom-card__category {
    display: block;

    margin-bottom: 0.625em;

    color: var(--text-3);

    font-family: var(--font-tech);
    font-size: 0.5625em;
    font-weight: 600;
    -letter-spacing: 0.09375em;

    text-transform: uppercase;
}

.showroom-card__link {
    display: flex;
    align-items: center;
    justify-content: space-between;

    gap: 1.125em;

    width: 100%;

    margin-top: auto;

    padding-top: 1.125em;

    border-top:
        0.0625em solid
        var(--border-1);

    color: var(--text-1);

    font-family: var(--font-tech);
    font-size: 0.625em;
    font-weight: 600;
    -letter-spacing: 0.08125em;

    text-decoration: none;
    text-transform: uppercase;

    transition:
        color .3s ease;

    span {
        color: var(--text-3);

        font-size: 1.0625em;

        transition:
            transform .3s ease;
    }

    &:hover {
        color: var(--text-3);

        span {
            transform:
                translate(0.1875em, -0.1875em);
        }
    }
}

@media (min-width: 43.75em) {
    .showroom {
        padding:
            6.5625em 2.5em
            7.1875em;
    }

    .showroom__grid {
        grid-template-columns:
            repeat(2, minmax(0, 1fr));
    }

    .showroom-card:last-child {
        grid-column:
            1 / -1;

        max-width: 50%;

        width: calc(50% - 0.6875em);

        justify-self: center;
    }
}

@media (min-width: 65.625em) {
    .showroom {
        padding:
            7.5em 3.125em
            8.125em;
    }

    .showroom__grid {
        grid-template-columns:
            repeat(3, minmax(0, 1fr));

        gap: 1.5em;
    }

    .showroom-card:last-child {
        grid-column: auto;

        max-width: none;
        width: auto;
    }
}

@media (min-width: 87.5em) {
    .showroom {
        padding-left: 3.75em;
        padding-right: 3.75em;
    }
}
</style>